import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Eye, Edit3, EyeOff, Save, ShieldCheck,
  Building2, UserCheck, LayoutDashboard, DollarSign,
  Wallet, BarChart2, Megaphone, CheckCircle2, Lock, Play
} from "lucide-react";

import { getSidebarNav } from "@/components/layout/AppLayout";
import { useSession } from "@/lib/SessionContext";

const ACCESS_OPTIONS = [
  { value: "none", label: "Tidak bisa akses", icon: EyeOff, cls: "text-slate-400 bg-slate-50 border-slate-200" },
  { value: "view", label: "Hanya lihat", icon: Eye, cls: "text-blue-700 bg-blue-50 border-blue-200 font-bold" },
  { value: "edit", label: "Bisa edit", icon: Edit3, cls: "text-emerald-700 bg-emerald-50 border-emerald-300 font-bold" },
];

const PAGES_WITH_PRICE = [
  'stok_inventori', 'marketing_inventory', 'purchasing_inventory', 'kalkulator_hpp', 'purchasing_items', 'riwayat_hpp',
  'laporan', 'biaya_operasional', 'database_brand', 'dashboard_finance',
  'rekening_mutasi', 'finance_calculator', 'finance_sph_list', 'invoice_po', 'finance_custom_invoices',
  'finance_payables', 'finance_receivables', 'finance_payables_receivables',
  'purchasing_payables', 'purchasing_requests', 'finance_purchasing_requests',
  'analytics_margin_keseluruhan', 'analytics_laporan_keuangan', 'analytics_margin_po', 'analytics_margin_sample',
  'analytics_margin_custom', 'analytics_ranking', 'analytics_omset', 'biaya_produksi', 'gaji_karyawan', 'finance_laporan',
  'marketing_calculator', 'marketing_sph_list', 'marketing_invoices', 'marketing_custom_invoices', 'marketing_receivables',
  'barang_harus_dibeli', 'purchasing_items_to_buy', 'purchasing_items_po'
];

const PAGES_WITH_PIC = [
  'po_produksi', 'invoice_po', 'finance_custom_invoices', 'marketing_calculator', 'marketing_sph_list',
  'marketing_invoices', 'marketing_custom_invoices', 'sample_racikan', 'data_klien', 'purchasing_client_data',
  'database_brand', 'delivery', 'purchasing_delivery', 'tugas_waktu_produksi', 'tugas_saya', 'return',
  'produksi_reject', 'finance_reject_tracking', 'barang_harus_dibeli', 'purchasing_items_to_buy',
  'purchasing_items_po', 'purchasing_requests', 'finance_purchasing_requests'
];

export default function PageAccessDialog({ open, member, onClose, onSaved }) {
  const [access, setAccess] = useState({});
  const [loading, setLoading] = useState(false);
  const [activePortalIndex, setActivePortalIndex] = useState(0);
  const { member: currentMember, refreshMember } = useSession();

  useEffect(() => {
    if (member) {
      setAccess(member.page_access || {});
    }
  }, [member]);

  const portals = [
    { title: "Portal Workspace", icon: LayoutDashboard, accessKey: "portal_workspace", nav: getSidebarNav(null, '/office/workspace') },
    { title: "Portal Finance", icon: DollarSign, accessKey: "portal_finance", nav: getSidebarNav(null, '/office/finance') },
    { title: "Portal Purchasing", icon: Wallet, accessKey: "portal_purchasing", nav: getSidebarNav(null, '/office/purchasing') },
    { title: "Portal Analytics Management", icon: BarChart2, accessKey: "portal_analytics", nav: getSidebarNav(null, '/office/analytics') },
    { title: "Portal Produksi", icon: Building2, accessKey: "portal_production", nav: getSidebarNav(null, '/office/production') },
    { title: "Portal Marketing", icon: Megaphone, accessKey: "portal_marketing", nav: getSidebarNav(null, '/office/marketing') },
  ];

  // Extract all valid access keys to enable "Set All" functionality
  const allKeys = [
    "portal_workspace", "portal_finance", "portal_production", "portal_marketing", "portal_purchasing", "portal_analytics",
    ...portals.flatMap(p => p.nav.flatMap(g => g.items))
             .filter(item => item.accessKey)
             .map(item => item.accessKey)
  ];

  const toggle = (key) => {
    setAccess(prev => {
      const current = prev[key] || "none";
      const next = current === "none" ? "view" : current === "view" ? "edit" : "none";
      return { ...prev, [key]: next };
    });
  };

  const togglePortal = (key) => {
    setAccess(prev => {
      const current = prev[key] || "none";
      const next = current === "none" ? "edit" : "none";
      return { ...prev, [key]: next };
    });
  };

  const setPortalAll = (portalItems, val) => {
    setAccess(prev => {
      const next = { ...prev };
      portalItems.forEach(item => {
        next[item.accessKey] = val;
      });
      return next;
    });
  };

  const togglePrice = (key) => {
    setAccess(prev => ({ ...prev, [`${key}_show_price`]: !prev[`${key}_show_price`] }));
  };

  const togglePic = (key) => {
    setAccess(prev => {
      if (key === 'sample_racikan') {
        const current = prev[`${key}_is_pic`];
        let next;
        if (!current) next = 'brand';
        else if (current === 'brand') next = 'pembuat';
        else if (current === 'pembuat') next = 'both';
        else next = false;
        return { ...prev, [`${key}_is_pic`]: next };
      }
      if (key === 'tugas_saya') {
        const current = prev[`${key}_is_pic`];
        let next;
        if (!current || current === false) next = 'mulai';
        else if (current === 'mulai' || current === true) next = 'memantau';
        else if (current === 'memantau') next = 'validasi';
        else if (current === 'validasi') next = 'all';
        else next = false;
        return { ...prev, [`${key}_is_pic`]: next };
      }
      return { ...prev, [`${key}_is_pic`]: !prev[`${key}_is_pic`] };
    });
  };

  const handleSave = async () => {
    setLoading(true);
    await base44.entities.TeamMember.update(member.id, { page_access: access });
    if (currentMember?.id === member.id) {
      await refreshMember();
    }
    setLoading(false);
    onSaved?.();
    onClose();
  };

  const setAll = (val) => {
    const newAccess = {};
    allKeys.forEach(k => { newAccess[k] = val; });
    setAccess(newAccess);
  };

  if (!member) return null;

  const currentPortal = portals[activePortalIndex] || portals[0];
  const portalItems = currentPortal.nav.flatMap(g => g.items).filter(item => item.accessKey);
  const uniqueItems = Array.from(new Map(portalItems.map(item => [item.accessKey, item])).values());
  const currentPortalAccess = access[currentPortal.accessKey] || "none";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden bg-[#FFFDF9] border-[#E6D5B8]">
        {/* Header */}
        <DialogHeader className="p-5 pb-3 border-b border-[#E6D5B8]/40 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg font-black text-[#302621]">Akses Halaman — {member.full_name}</DialogTitle>
              <p className="text-xs text-[#7A695B] mt-0.5">
                Pilih portal terlebih dahulu, lalu atur detail tingkat akses & tombol <strong>Set PIC</strong> di bawahnya.
              </p>
            </div>
            <Badge variant="outline" className="text-xs capitalize font-bold bg-[#F9F5EC] border-[#E6D5B8] text-[#8C6208]">
              {member.position || member.role} · {member.department || "Tim Operasional"}
            </Badge>
          </div>

          {/* Quick All Buttons */}
          <div className="flex gap-2 pt-2.5 flex-wrap">
            <Button variant="outline" size="sm" className="text-xs h-7 border-[#E6D5B8] text-[#7A695B] hover:bg-[#F9F5EC]" onClick={() => setAll("edit")}>
              Semua Portal Bisa Edit
            </Button>
            <Button variant="outline" size="sm" className="text-xs h-7 border-[#E6D5B8] text-[#7A695B] hover:bg-[#F9F5EC]" onClick={() => setAll("view")}>
              Semua Portal Hanya Lihat
            </Button>
            <Button variant="outline" size="sm" className="text-xs h-7 border-[#E6D5B8] text-[#7A695B] hover:bg-[#F9F5EC]" onClick={() => setAll("none")}>
              Kunci Semua Portal
            </Button>
          </div>
        </DialogHeader>

        {/* Portal Selector Tabs */}
        <div className="px-5 py-3 bg-[#F9F5EC]/60 border-b border-[#E6D5B8]/50">
          <p className="text-[10px] font-black uppercase tracking-wider text-[#A39171] mb-2">Pilih Portal yang Ingin Diatur:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {portals.map((p, idx) => {
              const PIcon = p.icon;
              const isActive = activePortalIndex === idx;
              const pAccess = access[p.accessKey] || "none";
              const pItems = p.nav.flatMap(g => g.items).filter(item => item.accessKey);
              const pUnique = Array.from(new Map(pItems.map(item => [item.accessKey, item])).values());
              const activeCount = pUnique.filter(item => (access[item.accessKey] || 'none') !== 'none').length;

              return (
                <button
                  key={p.title}
                  type="button"
                  onClick={() => setActivePortalIndex(idx)}
                  className={`flex items-center gap-2.5 p-2.5 rounded-xl border text-left transition-all ${
                    isActive 
                      ? 'border-[#8C6208] bg-white shadow-md ring-2 ring-[#8C6208]/20 font-bold text-[#302621]' 
                      : 'border-[#E6D5B8]/60 bg-white/70 hover:bg-white text-slate-600'
                  }`}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${isActive ? 'bg-[#8C6208] text-white' : 'bg-muted text-muted-foreground'}`}>
                    <PIcon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs truncate font-bold">{p.title.replace('Portal ', '')}</p>
                    <span className={`text-[10px] font-medium ${activeCount > 0 ? 'text-emerald-700 font-bold' : 'text-slate-400'}`}>
                      {activeCount > 0 ? `✓ ${activeCount} Aktif` : 'Terkunci'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Portal Detail Content Area */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="bg-white rounded-2xl border border-[#E6D5B8] p-4 space-y-3 shadow-2xs">
            {/* Header Active Portal */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 border-b border-[#E6D5B8]/40 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-[#8C6208]/10 text-[#8C6208] flex items-center justify-center font-bold">
                  <currentPortal.icon className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-extrabold text-sm text-[#302621]">{currentPortal.title}</h3>
                  <p className="text-xs text-[#7A695B]">Pengaturan hak akses halaman & tombol Set PIC untuk {currentPortal.title}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1 md:pt-0">
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setPortalAll(uniqueItems, 'edit')} 
                  className="text-xs h-7 border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 font-bold"
                >
                  Semua Edit
                </Button>
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setPortalAll(uniqueItems, 'view')} 
                  className="text-xs h-7 border-blue-300 text-blue-800 bg-blue-50 hover:bg-blue-100 font-bold"
                >
                  Semua Lihat
                </Button>
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setPortalAll(uniqueItems, 'none')} 
                  className="text-xs h-7 border-slate-300 text-slate-700 bg-slate-50 hover:bg-slate-100"
                >
                  Kunci Portal
                </Button>
              </div>
            </div>

            {/* Master Portal Switch */}
            <button
              type="button"
              onClick={() => togglePortal(currentPortal.accessKey)}
              className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl border border-[#D4AF37]/50 bg-[#F9F5EC] hover:bg-[#F2ECE4] transition-colors text-left"
            >
              <Building2 className="w-4 h-4 shrink-0 text-[#8C6208]" />
              <span className="flex-1 text-xs font-black text-[#8C6208]">AKSES MASUK {currentPortal.title.toUpperCase()}</span>
              <div className={`flex items-center gap-1.5 text-xs font-black ${currentPortalAccess === 'none' ? 'text-red-600' : 'text-emerald-700'}`}>
                {currentPortalAccess === 'none' ? <Lock className="w-3.5 h-3.5 text-red-500" /> : <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />}
                <span>{currentPortalAccess === 'none' ? 'Terkunci' : 'Terbuka'}</span>
              </div>
            </button>

            {/* List of Pages in Selected Portal */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              {uniqueItems.map(page => {
                const currentAccess = access[page.accessKey] || "none";
                const opt = ACCESS_OPTIONS.find(o => o.value === currentAccess);
                const AccessIcon = opt.icon;
                const PageIcon = page.icon;
                const currentPicAccess = !!access[`${page.accessKey}_is_pic`];
                const currentPriceAccess = !!access[`${page.accessKey}_show_price`];

                return (
                  <div
                    key={page.accessKey}
                    className="flex flex-col gap-2 p-3 rounded-xl border border-[#E6D5B8]/60 bg-white hover:border-[#8C6208]/40 transition-all shadow-2xs"
                  >
                    <div className="flex items-center gap-2.5 w-full">
                      <div className="w-6 h-6 rounded-lg bg-muted/60 flex items-center justify-center shrink-0">
                        <PageIcon className="w-3.5 h-3.5 text-[#A39171]" />
                      </div>
                      <span className="flex-1 text-xs font-bold text-[#302621] truncate">{page.label}</span>
                    </div>

                    <div className="flex items-center gap-1.5 w-full pt-1">
                      {/* Access level dropdown / toggle */}
                      <button
                        type="button"
                        onClick={() => toggle(page.accessKey)}
                        className={`flex-1 flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border transition-all ${opt.cls}`}
                      >
                        <AccessIcon className="w-3.5 h-3.5" />
                        <span>{opt.label}</span>
                      </button>

                      {/* Set PIC button */}
                      {PAGES_WITH_PIC.includes(page.accessKey) && (
                        <button
                          type="button"
                          onClick={() => togglePic(page.accessKey)}
                          disabled={currentAccess === 'none'}
                          className={`flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border font-bold transition-all ${
                            currentAccess === 'none'
                              ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed opacity-40'
                              : page.accessKey === 'tugas_saya' && (access[`${page.accessKey}_is_pic`] === 'all' || access[`${page.accessKey}_is_pic`] === 'both')
                                ? 'border-indigo-400 bg-gradient-to-r from-emerald-50 to-indigo-50 text-indigo-950 shadow-2xs font-extrabold ring-1 ring-indigo-400/50'
                                : page.accessKey === 'tugas_saya' && access[`${page.accessKey}_is_pic`] === 'memantau'
                                  ? 'border-blue-400 bg-blue-100 text-blue-900 shadow-2xs font-extrabold ring-1 ring-blue-400/50'
                                  : page.accessKey === 'tugas_saya' && access[`${page.accessKey}_is_pic`] === 'validasi'
                                    ? 'border-purple-400 bg-purple-100 text-purple-900 shadow-2xs font-extrabold ring-1 ring-purple-400/50'
                                    : page.accessKey === 'tugas_saya' && (access[`${page.accessKey}_is_pic`] === 'mulai' || access[`${page.accessKey}_is_pic`] === true)
                                      ? 'border-emerald-400 bg-emerald-100 text-emerald-900 shadow-2xs font-extrabold ring-1 ring-emerald-400/50'
                                      : currentPicAccess 
                                        ? 'border-amber-400 bg-amber-100 text-amber-900 shadow-2xs font-extrabold ring-1 ring-amber-400/50' 
                                        : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100'
                          }`}
                          title={`Izinkan Karyawan ini dipilih sebagai PIC untuk ${page.label}`}
                        >
                          <UserCheck className="w-3.5 h-3.5" />
                          <span>
                            {page.accessKey === 'sample_racikan' && access[`${page.accessKey}_is_pic`] === 'brand' ? 'PIC Brand' :
                             page.accessKey === 'sample_racikan' && access[`${page.accessKey}_is_pic`] === 'pembuat' ? 'PIC Pembuat' :
                             page.accessKey === 'sample_racikan' && (access[`${page.accessKey}_is_pic`] === 'both' || access[`${page.accessKey}_is_pic`] === true) ? 'PIC Keduanya' :
                             page.accessKey === 'tugas_saya' && (access[`${page.accessKey}_is_pic`] === 'all' || access[`${page.accessKey}_is_pic`] === 'both') ? 'PIC & Validasi' :
                             page.accessKey === 'tugas_saya' && (access[`${page.accessKey}_is_pic`] === 'mulai' || access[`${page.accessKey}_is_pic`] === true) ? 'PIC Mulai' :
                             page.accessKey === 'tugas_saya' && access[`${page.accessKey}_is_pic`] === 'memantau' ? 'Memantau' :
                             page.accessKey === 'tugas_saya' && access[`${page.accessKey}_is_pic`] === 'validasi' ? 'Validasi QC' :
                             'Set PIC'}
                          </span>
                        </button>
                      )}

                      {/* Show Price button */}
                      {PAGES_WITH_PRICE.includes(page.accessKey) && (
                        <button
                          type="button"
                          onClick={() => togglePrice(page.accessKey)}
                          className={`flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-lg border transition-colors ${currentPriceAccess ? 'border-amber-300 bg-amber-50 text-amber-800 font-bold' : 'border-slate-200 bg-slate-50 text-slate-400'}`}
                          title="Tampilkan / Sembunyikan Harga"
                        >
                          {currentPriceAccess ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          <span>Harga</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 bg-white border-t border-[#E6D5B8]/40">
          <Button variant="outline" className="flex-1 border-[#E6D5B8] text-[#7A695B] hover:bg-[#F9F5EC]" onClick={onClose}>Batal</Button>
          <Button className="flex-1 gap-2 bg-[#423127] hover:bg-[#302621] text-white shadow-md shadow-[#423127]/20" onClick={handleSave} disabled={loading}>
            <Save className="w-4 h-4 text-[#D4AF37]" />
            {loading ? "Menyimpan..." : "Simpan Hak Akses"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}